import type { ProjectEvaluation, BatchJobAcceptedResponse, BatchJobStatusResponse, ProjectOverridePayload, ProjectOverride } from '../shared/types';
import { authenticatedFetch } from '../shared/authenticatedFetch';
import { ApiError } from '../shared/apiError';

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_DURATION_MS = 5 * 60 * 1000;

export async function getProjectEvaluations(
  apiUrl: string,
  fiscalYearQuarter: string
): Promise<ProjectEvaluation[]> {
  try {
    const response = await authenticatedFetch(
      `${apiUrl}/project-evaluations?fiscalYearQuarter=${encodeURIComponent(fiscalYearQuarter)}`
    );

    const data: ProjectEvaluation[] = await response.json();
    return data;
  } catch (error) {
    if (error instanceof ApiError && error.isUnauthorized) {
      throw error;
    }
    return [];
  }
}

export async function triggerCapexEvaluation(
  apiUrl: string,
  fiscalYearQuarter: string,
  areaLevel1?: string
): Promise<BatchJobAcceptedResponse> {
  let url = `${apiUrl}/project-evaluations/capex?fiscalYearQuarter=${encodeURIComponent(fiscalYearQuarter)}`;
  if (areaLevel1) {
    url += `&areaLevel1=${encodeURIComponent(areaLevel1)}`;
  }
  const response = await authenticatedFetch(url, { method: 'POST' });

  const data: BatchJobAcceptedResponse = await response.json();
  return data;
}

export async function triggerRndEvaluation(
  apiUrl: string,
  fiscalYearQuarter: string,
  areaLevel1?: string
): Promise<BatchJobAcceptedResponse> {
  let url = `${apiUrl}/project-evaluations/rnd?fiscalYearQuarter=${encodeURIComponent(fiscalYearQuarter)}`;
  if (areaLevel1) {
    url += `&areaLevel1=${encodeURIComponent(areaLevel1)}`;
  }
  const response = await authenticatedFetch(url, { method: 'POST' });

  const data: BatchJobAcceptedResponse = await response.json();
  return data;
}

export async function updateJustification(
  apiUrl: string,
  areaLevel1: string,
  fiscalYearQuarter: string,
  type: 'capex' | 'rnd',
  justification: string
): Promise<ProjectEvaluation> {
  const response = await authenticatedFetch(
    `${apiUrl}/project-evaluations/justification?areaLevel1=${encodeURIComponent(areaLevel1)}&fiscalYearQuarter=${encodeURIComponent(fiscalYearQuarter)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, justification }),
    }
  );
  return await response.json();
}

export async function updateProjectOverride(
  apiUrl: string,
  areaLevel1: string,
  fiscalYearQuarter: string,
  payload: ProjectOverridePayload
): Promise<ProjectOverride> {
  const response = await authenticatedFetch(
    `${apiUrl}/project-evaluations/overrides?areaLevel1=${encodeURIComponent(areaLevel1)}&fiscalYearQuarter=${encodeURIComponent(fiscalYearQuarter)}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  );

  const data: ProjectOverride = await response.json();
  return data;
}

export async function getEvaluationJobStatus(
  apiUrl: string,
  jobId: string,
  signal?: AbortSignal
): Promise<BatchJobStatusResponse> {
  const response = await authenticatedFetch(`${apiUrl}/assessments/batch/${jobId}`, { signal });
  return response.json();
}

export async function pollEvaluationJobUntilDone(
  apiUrl: string,
  jobId: string,
  signal?: AbortSignal
): Promise<BatchJobStatusResponse> {
  const startTime = Date.now();

  while (Date.now() - startTime < MAX_POLL_DURATION_MS) {
    signal?.throwIfAborted();

    const status = await getEvaluationJobStatus(apiUrl, jobId, signal);

    if (status.status === 'completed' || status.status === 'failed') {
      return status;
    }

    await new Promise((resolve, reject) => {
      const timer = setTimeout(resolve, POLL_INTERVAL_MS);
      signal?.addEventListener('abort', () => {
        clearTimeout(timer);
        reject(signal.reason);
      }, { once: true });
    });
  }

  throw new Error('Evaluation job timed out after 5 minutes. The job may still be processing in the background.');
}
